import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import Sidebar from './Sidebar'
import { ROUTES } from '../../routes/routeConfig'
import { MenuIcon, MyProfileIcon } from '../../assets/icons'
import { PWC } from '../../assets/icons/PWC'
import { UserIcon } from '../../assets/icons/UserIcon'
import { useSidebar } from '../../contexts/SidebarContext'
import {
  clearSessionAuth,
  getAccessToken,
  setAccessToken,
  setRefreshToken,
  getRefreshToken,
  getUsername,
} from '../../utils/session'
import { refreshToken, logoutUser } from '../../api/auth'
import { getUserRole } from '../../api/auth.js'
import { IconButton, Menu, MenuItem, ListItemIcon, Divider } from "@mui/material";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useFullScreen } from '../../contexts/FullScreenContext'
import { useDispatch, useSelector } from 'react-redux'
import { resetEntityTanState, resetSelections } from "../../store/slice/entityTanSlice.js"
import { setUserRole, clearUserRole } from "../../store/slice/userSlice.js"
import Footer from "./Footer"
import { GLOBAL_CONSTANTS } from '../../constants/globalConstants.js'
import { sanitizeForLog } from '../../constants/index'
import Header from './Header.jsx'

const Layout = ({ children }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [user, setUser] = useState('')
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const { exitFullScreen } = useFullScreen()
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar()

  const userRole = useSelector(state => state.user.role)

  const handleClick = event => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)

  const refreshAccessToken = async () => {
    try {
      const refreshTokenValue = getRefreshToken()
      if (refreshTokenValue) {
        const tokenData = await refreshToken()
        setAccessToken(tokenData.access_token, Number(tokenData.expires_in))
        setRefreshToken(tokenData.refresh_token)
      }
    } catch (error) {
      console.error('Error refreshing access token:')
      clearSessionAuth()
      navigate(ROUTES.LOGIN)
    }
  }

  // 🔹 Fetch and store user role in Redux
  const fetchUserRole = async () => {
    try {
      const roleData = await getUserRole()
      if (roleData) {
        dispatch(setUserRole(roleData))
      }
    } catch (error) {
      console.error('Error fetching user role:', sanitizeForLog(error?.message || 'Unknown error'))
      dispatch(clearUserRole())
    }
  }

  useEffect(() => {
    const checkTokenExpiration = () => {
      const authToken = getAccessToken()
      const refreshTokenValue = getRefreshToken()

      if (!authToken || !refreshTokenValue) {
        navigate(ROUTES.LOGIN)
        return
      }

      const tokenExpTime = sessionStorage.getItem('tokenExpiresIn')
      const currentTime = Math.floor(Date.now() / 1000)

      if (currentTime >= tokenExpTime) {
        refreshAccessToken()
      }
    }

    const storedUsername = getUsername()
    if (storedUsername) setUser(storedUsername)

    // Initial token check
    checkTokenExpiration()

    // 🔹 Fetch role after successful token validation
    fetchUserRole()

    const interval = setInterval(checkTokenExpiration, 60000)
    return () => clearInterval(interval)
  }, [navigate])

  const handleLogout = async () => {
    try {
      await logoutUser()
      clearSessionAuth()
      dispatch(resetEntityTanState())
      dispatch(resetSelections())
      dispatch(clearUserRole())
      navigate(ROUTES.LOGIN)
    } catch (error) {
      console.log(error, 'error')
    }
  }

  // 🔹 Inactivity logout
  useEffect(() => {
    let inactivityTimer
    const resetTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer)
      inactivityTimer = setTimeout(() => handleLogout(), GLOBAL_CONSTANTS.INACTIVITY_LIMIT)
    }

    const events = ['mousemove', 'mousedown', 'click', 'scroll', 'keydown', 'touchstart']
    events.forEach(e => window.addEventListener(e, resetTimer))
    resetTimer()

    return () => {
      events.forEach(e => window.removeEventListener(e, resetTimer))
      if (inactivityTimer) clearTimeout(inactivityTimer)
    }
  }, [handleLogout])

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    )
  }

  return (
    <div className="h-screen flex overflow-hidden">
      <Sidebar
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
        exitFullScreen={exitFullScreen}
        userRole={'admin'}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header
          setIsMobileOpen={setIsMobileOpen}
          user={user}
          handleClick={handleClick}
          anchorEl={anchorEl}
          open={open}
          handleClose={handleClose}
          handleLogout={handleLogout}
        />

        <main className="flex-1 border-b border-[var(--border-subtel)] overflow-auto px-[15px] bg-[#ffffff] hide-scrollbar">
          {children}
        </main>

        <footer className=" flex-shrink-0 -mt-[2px]">
          <Footer />
        </footer>
      </div>
    </div>
  )
}

export default Layout
